"use client";

import React, { useState, useEffect, useRef } from "react";
import { Terminal, Cpu, CircleX, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function TerminalForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    formId
  } = useWvcForm();
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [glowColor, setGlowColor] = useState("#DC143C");
  const [showShockwave, setShowShockwave] = useState(false);
  const [showMatrixSuccess, setShowMatrixSuccess] = useState(false);
  useEffect(() => {
    if (!focusedField) {
      setGlowColor("#DC143C");
      return;
    }
    const fieldState = control.getFieldState(focusedField);
    if (fieldState.invalid) {
      setGlowColor("#DC143C");
    } else {
      setGlowColor("#E6D5AC");
    }
  }, [focusedField, control, control._formState.errors]);
  useEffect(() => {
    if (isSubmitted && !submitError) {
      setShowShockwave(true);
      const shockwaveTimer = setTimeout(() => {
        setShowShockwave(false);
        setShowMatrixSuccess(true);
      }, 1000);
      return () => clearTimeout(shockwaveTimer);
    }
  }, [isSubmitted, submitError]);
  if (showMatrixSuccess) {
    return <div data-section-id="131288" className="flex flex-col items-center justify-center min-h-[400px] font-mono animate-in fade-in duration-1000">
        <div className="text-[#DC143C] text-2xl md:text-4xl font-serif font-bold tracking-widest mb-6 drop-shadow-[0_0_15px_rgba(220,20,60,0.8)]">
          &gt; ACCESSO_AUTORIZZATO
        </div>
        <p className="text-white/70 text-lg mb-8 animate-pulse text-center max-w-md">
          {successMessage}
        </p>
        <div className="flex items-center gap-2 text-[#E6D5AC]/50 text-sm">
          <CircleCheck className="size-4" />
          <span>Infrastruttura blindata attiva. In attesa di contatto prioritario...</span>
        </div>
      </div>;
  }
  return <div className="relative w-full max-w-3xl mx-auto">
      {}
      <div className="fixed inset-0 pointer-events-none transition-colors duration-700 ease-in-out opacity-10 z-0" style={{
      background: `radial-gradient(circle at 50% 50%, ${glowColor} 0%, transparent 60%)`
    }} />

      {}
      {showShockwave && <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none overflow-hidden">
          <div className="w-4 h-4 bg-white rounded-full animate-[shockwave_1s_ease-out_forwards]" />
        </div>}

      {}
      <div className="relative z-10 bg-black/70 backdrop-blur-3xl border border-white/10 shadow-[0_0_60px_rgba(0,0,0,0.8)] overflow-hidden">
        {}
        <div className="flex items-center justify-between px-4 py-2 bg-white/5 border-b border-white/10">
          <div className="flex items-center gap-2 text-[#E6D5AC]/70 font-mono text-xs">
            <Terminal className="size-4" />
            <span>geroi@terminal:~# ./audit_prequalificazione.sh</span>
          </div>
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-none bg-[#DC143C]/50" />
            <div className="w-3 h-3 rounded-none bg-[#E6D5AC]/50" />
            <div className="w-3 h-3 rounded-none bg-white/50" />
          </div>
        </div>

        {}
        <div className="p-6 md:p-12">
          <Form onSubmit={handleSubmit} className="space-y-10">
            
            {}
            <FormField control={control} name="name" rules={{
            required: "Il dominio aziendale è richiesto per l'analisi preliminare."
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-serif text-[#E6D5AC] uppercase tracking-[0.2em] text-xs">
                    [PARAM: DOMINIO_AZIENDALE]
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-baseline font-mono text-sm md:text-base group">
                      <span className="text-white/30 mr-2 select-none group-focus-within:text-[#E6D5AC] transition-colors">
                        geroi@system:~$
                      </span>
                      <Input {...field} placeholder="" onFocus={() => setFocusedField("name")} onBlur={() => setFocusedField(null)} className="flex-1 border-0 border-b-2 border-white/10 rounded-none bg-transparent px-0 py-1 shadow-none focus-visible:ring-0 focus-visible:border-[#DC143C] focus-visible:shadow-none transition-all placeholder:text-white/10" />
                    </div>
                  </FormControl>
                  <FormMessage className="font-mono text-xs mt-1 text-[#DC143C]" />
                </FormItem>} />

            {}
            <FormField control={control} name="email" rules={{
            required: "Email istituzionale richiesta per il routing sicuro.",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Protocollo email non valido. Ricalibrare input."
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-serif text-[#E6D5AC] uppercase tracking-[0.2em] text-xs">
                    [PARAM: EMAIL_ISTITUZIONALE]
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-baseline font-mono text-sm md:text-base group">
                      <span className="text-white/30 mr-2 select-none group-focus-within:text-[#E6D5AC] transition-colors">
                        geroi@system:~$
                      </span>
                      <Input {...field} type="email" placeholder="" onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField(null)} className="flex-1 border-0 border-b-2 border-white/10 rounded-none bg-transparent px-0 py-1 shadow-none focus-visible:ring-0 focus-visible:border-[#DC143C] focus-visible:shadow-none transition-all placeholder:text-white/10" />
                    </div>
                  </FormControl>
                  <FormMessage className="font-mono text-xs mt-1 text-[#DC143C]" />
                </FormItem>} />

            {}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <FormField control={control} name="phone" rules={{
              required: "Parametro budget richiesto.",
              pattern: {
                value: /^[0-9+\-\s()]*$/,
                message: "Formato numerico non valido."
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-serif text-[#E6D5AC] uppercase tracking-[0.2em] text-xs">
                      [PARAM: BUDGET_ANNUALE]
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-baseline font-mono text-sm md:text-base group">
                        <span className="text-white/30 mr-2 select-none group-focus-within:text-[#E6D5AC] transition-colors">
                          geroi@system:~$
                        </span>
                        <Input {...field} type="tel" placeholder="" onFocus={() => setFocusedField("phone")} onBlur={() => setFocusedField(null)} className="flex-1 border-0 border-b-2 border-white/10 rounded-none bg-transparent px-0 py-1 shadow-none focus-visible:ring-0 focus-visible:border-[#DC143C] focus-visible:shadow-none transition-all placeholder:text-white/10" />
                      </div>
                    </FormControl>
                    <FormMessage className="font-mono text-xs mt-1 text-[#DC143C]" />
                  </FormItem>} />

              <FormField control={control} name="website_url" rules={{
              required: "Mercato target obbligatorio per il matchmaking."
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="font-serif text-[#E6D5AC] uppercase tracking-[0.2em] text-xs">
                      [PARAM: MERCATO_TARGET]
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-baseline font-mono text-sm md:text-base group">
                        <span className="text-white/30 mr-2 select-none group-focus-within:text-[#E6D5AC] transition-colors">
                          geroi@system:~$
                        </span>
                        <Input {...field} type="text" placeholder="" onFocus={() => setFocusedField("website_url")} onBlur={() => setFocusedField(null)} className="flex-1 border-0 border-b-2 border-white/10 rounded-none bg-transparent px-0 py-1 shadow-none focus-visible:ring-0 focus-visible:border-[#DC143C] focus-visible:shadow-none transition-all placeholder:text-white/10" />
                      </div>
                    </FormControl>
                    <FormMessage className="font-mono text-xs mt-1 text-[#DC143C]" />
                  </FormItem>} />
            </div>

            {}
            <FormField control={control} name="project_brief" rules={{
            required: "Obiettivi strategici obbligatori per la valutazione d'élite.",
            minLength: {
              value: 20,
              message: "Dati insufficienti per l'analisi di scenario."
            },
            maxLength: {
              value: 500,
              message: "Buffer in overflow. Sintetizzare obiettivi."
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-serif text-[#E6D5AC] uppercase tracking-[0.2em] text-xs">
                    [PAYLOAD: OBIETTIVI_STRATEGICI]
                  </FormLabel>
                  <FormControl>
                    <div className="flex flex-col font-mono text-sm md:text-base group">
                      <span className="text-white/30 mb-2 select-none group-focus-within:text-[#E6D5AC] transition-colors">
                        geroi@system:~$ cat &gt; mission_profile.txt
                      </span>
                      <Textarea {...field} placeholder="" onFocus={() => setFocusedField("project_brief")} onBlur={() => setFocusedField(null)} className="min-h-[120px] border border-white/10 rounded-none bg-white/5 p-4 shadow-none focus-visible:ring-0 focus-visible:border-[#DC143C] focus-visible:shadow-[0_0_20px_rgba(220,20,60,0.1)] transition-all placeholder:text-white/10 resize-none" />
                    </div>
                  </FormControl>
                  <FormMessage className="font-mono text-xs mt-1 text-[#DC143C]" />
                </FormItem>} />

            {}
            {submitError && <div className="p-4 bg-[#DC143C]/10 border border-[#DC143C]/50 flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                <CircleX className="size-5 text-[#DC143C] shrink-0 mt-0.5" />
                <div className="font-mono text-sm">
                  <p className="text-[#DC143C] font-bold uppercase tracking-wider mb-1">ERRORE_SISTEMA_CRITICO</p>
                  <p className="text-[#DC143C]/80">{submitError}</p>
                </div>
              </div>}

            {}
            <div className="pt-6 flex flex-col items-center gap-6">
              <Button type="submit" disabled={isSubmitting} className="relative group overflow-hidden rounded-none border-none bg-[#DC143C] hover:bg-[#DC143C]/90 text-white font-serif text-xl font-bold tracking-[0.15em] px-16 py-8 transition-all duration-300 shadow-[0_0_40px_rgba(220,20,60,0.3)]">
                {}
                <span className="absolute inset-0 w-full h-full bg-white/10 translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-out" />
                
                <span className="relative flex items-center gap-4">
                  {isSubmitting ? <>
                      <Cpu className="size-6 animate-spin" />
                      ELABORAZIONE...
                    </> : <>
                      RICHIEDI AUDIT DI SALVATAGGIO
                    </>}
                </span>
              </Button>

              <div className="text-center font-mono space-y-1">
                <p className="text-white/40 text-[10px] uppercase tracking-widest">Linea Diretta Operazioni Logistiche</p>
                <p className="text-[#E6D5AC] text-sm font-bold tracking-widest drop-shadow-[0_0_8px_rgba(230,213,172,0.4)]">
                  SOLO EMERGENZE CARGO: +39 02 8295 8642
                </p>
              </div>
            </div>

          </Form>
        </div>
      </div>
    </div>;
}
export default function InitializeConnection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="muro-di-pre-qualificazione" className="dark bg-[#0A0A0A] text-white relative min-h-screen py-32 px-4 md:px-8 overflow-hidden flex flex-col items-center justify-center">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes shockwave {
          0% { transform: scale(0); opacity: 0; background-color: transparent; }
          20% { transform: scale(1); opacity: 1; background-color: #fff; }
          80% { transform: scale(150); opacity: 1; background-color: #fff; }
          100% { transform: scale(200); opacity: 0; background-color: transparent; }
        }
        .watermark-text {
          position: absolute;
          font-family: var(--font-serif);
          font-weight: 900;
          font-size: 15vw;
          color: rgba(255, 255, 255, 0.02);
          white-space: nowrap;
          pointer-events: none;
          z-index: 0;
          user-select: none;
          text-transform: uppercase;
        }
      `
    }} />

      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none z-0" />
      
      <div className="watermark-text top-10 -left-20 rotate-12">TOP SECRET</div>
      <div className="watermark-text bottom-20 -right-20 -rotate-12">CONFIDENTIAL</div>

      <div ref={ref} className={`relative z-10 w-full max-w-5xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"}`}>
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-4xl md:text-7xl font-bold font-serif tracking-tighter mb-6 text-white">
            MURO DI <span className="text-[#E6D5AC]">PRE-QUALIFICAZIONE</span>
          </h2>
          <p className="text-white/50 font-sans text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Non accettiamo ogni sfida. Inserisci i parametri della tua infrastruttura per determinare se la tua operazione soddisfa i criteri di ingaggio d'élite.
          </p>
        </div>

        <FormProvider formKey="prequalification_form_home" formId="prequalification_form_home" sectionName="InitializeConnection" defaultValues={{
        name: "",
        email: "",
        phone: "",
        website_url: "",
        project_brief: ""
      }} submitText="RICHIEDI AUDIT" successMessage="I dati sono stati acquisiti dai nostri server blindati. Un Direttore Strategico valuterà la pratica entro 4 ore." errorMessage="Errore critico durante la trasmissione criptata. Verifica i parametri e riprova.">
          <TerminalForm />
        </FormProvider>
      </div>
    </section>;
}